import { useState, useRef, useEffect, useLayoutEffect } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { useQueryClient } from "@tanstack/react-query";
import { MoreHorizontal, Pencil, Trash2, Info, Loader2, UserPlus, CheckCircle, AlertTriangle, Send } from "lucide-react";
import { Button } from "@/components/ui/Button";
import { FormField } from "@/components/ui/FormField";
import { SelectField } from "@/components/ui/SelectField";
import { Badge } from "@/components/ui/Badge";
import { Modal } from "@/components/ui/Modal";
import { useProfiles, profilesKeys } from "@/hooks/useProfiles";
import { useInviteUser, useResendInvite } from "@/hooks/useInviteUser";
import { useDeleteUser, useAssignedBiensCount, useDeactivatePrestataire } from "@/hooks/useDeleteUser";
import { useMetiers } from "@/hooks/useMetiers";
import { profilesRepository } from "@/repositories/profilesRepository";
import type { ProfileRow } from "@/schemas/profile";
import { cn } from "@/lib/utils";

type BadgeVariant = "default" | "warning" | "accent" | "success" | "danger";

const ROLE_BADGE_VARIANT: Record<string, BadgeVariant> = {
  admin: "default",
  publication: "default",
  c_level: "default",
  collaborateur: "default",
  vente: "default",
  prestataire: "default",
  autre: "default",
  autres: "default",
  en_production: "warning",
};

const INTERNAL_ROLES = ["admin", "c_level", "publication", "collaborateur", "vente"];
const EXTERNAL_ROLES = ["prestataire", "autre", "autres"];

// Spécialités proposées pour un prestataire : uniquement PEB et ELS (état des
// lieux). Les autres métiers (nettoyage, photos, certificat élec) ne sont pas
// des prestataires gérés via ce flow. IDs alignés sur la table `metiers`.
const INVITE_SPECIALITE_IDS = ["expert-peb", "etat-des-lieux"];

function getInitials(prenom: string, nom: string): string {
  const first = prenom.charAt(0).toUpperCase();
  const last = nom.charAt(0).toUpperCase();
  return last ? `${first}${last}` : first;
}

// Bouton « … » + menu d'actions. Le menu est rendu dans un PORTAL en
// `position: fixed`, calé sur le bouton déclencheur. Indispensable : les cartes
// de section ont `overflow-hidden` (coins arrondis) → un menu `absolute` y serait
// coupé sur la dernière ligne d'une section. Le portal échappe à tout overflow.
function MemberActions({
  isOpen,
  onToggle,
  onEdit,
  onResendInvite,
  onDelete,
  onClose,
  resendDisabled,
  resending,
  deleteLabel,
}: {
  isOpen: boolean;
  onToggle: () => void;
  onEdit: () => void;
  onResendInvite?: () => void;
  onDelete: () => void;
  onClose: () => void;
  resendDisabled?: boolean;
  resending?: boolean;
  deleteLabel?: string;
}) {
  const { t } = useTranslation();
  const btnRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);

  // Positionne le menu sous (ou au-dessus de) le bouton, aligné à droite.
  useLayoutEffect(() => {
    if (!isOpen || !btnRef.current) return;
    const r = btnRef.current.getBoundingClientRect();
    const menuH = menuRef.current?.offsetHeight ?? 0;
    const MENU_W = 180;
    const openUp = r.bottom + menuH + 8 > window.innerHeight;
    setPos({
      top: openUp ? r.top - menuH - 4 : r.bottom + 4,
      left: r.right - MENU_W,
    });
  }, [isOpen]);

  // Ferme au clic dehors et au scroll (le menu fixed se détacherait sinon).
  useEffect(() => {
    if (!isOpen) return;
    const onDown = (e: MouseEvent) => {
      const tgt = e.target as Node;
      if (
        menuRef.current && !menuRef.current.contains(tgt) &&
        btnRef.current && !btnRef.current.contains(tgt)
      ) onClose();
    };
    document.addEventListener("mousedown", onDown);
    window.addEventListener("scroll", onClose, true);
    return () => {
      document.removeEventListener("mousedown", onDown);
      window.removeEventListener("scroll", onClose, true);
    };
  }, [isOpen, onClose]);

  return (
    <div className="relative inline-block">
      <button
        ref={btnRef}
        type="button"
        className={cn(
          "p-1 rounded hover:bg-gray-200 cursor-pointer transition-colors outline-none shadow-none",
          isOpen ? "bg-gray-200" : "",
        )}
        onClick={onToggle}
      >
        <MoreHorizontal size={16} className="text-text-muted" />
      </button>
      {isOpen && createPortal(
        <div
          ref={menuRef}
          style={{
            position: "fixed",
            top: pos?.top ?? -9999,
            left: pos?.left ?? -9999,
          }}
          className="bg-surface border border-border rounded-md shadow-lg z-50 min-w-[180px]"
        >
          <button
            type="button"
            className="w-full px-3 py-2 text-sm text-left flex items-center gap-2 hover:bg-gray-200 transition-colors cursor-pointer text-text-primary"
            onClick={onEdit}
          >
            <Pencil size={14} className="text-text-muted" />
            {t("common.edit")}
          </button>
          {onResendInvite && (
            <button
              type="button"
              className="w-full px-3 py-2 text-sm text-left flex items-center gap-2 hover:bg-gray-200 transition-colors cursor-pointer text-text-primary disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={resendDisabled || resending}
              onClick={onResendInvite}
            >
              {resending ? <Loader2 size={14} className="animate-spin text-text-muted" /> : <Send size={14} className="text-text-muted" />}
              {t("parametres.equipe.resendInvite")}
            </button>
          )}
          <button
            type="button"
            className="w-full px-3 py-2 text-sm text-left flex items-center gap-2 hover:bg-danger-light transition-colors cursor-pointer text-danger"
            onClick={onDelete}
          >
            <Trash2 size={14} />
            {deleteLabel ?? t("common.delete")}
          </button>
        </div>,
        document.body,
      )}
    </div>
  );
}

export function EquipePage() {
  const { t } = useTranslation();
  const qc = useQueryClient();

  const { data: profiles, isLoading, isError } = useProfiles();
  const members = profiles ?? [];

  // Liste des métiers (table `metiers`) pour le déroulant spécialité.
  // Restreint à PEB + ELS (cf. INVITE_SPECIALITE_IDS) — création ET édition.
  const { data: metiersData = [] } = useMetiers();
  const specialitesOptions = metiersData
    .filter((m) => m.actif !== false && INVITE_SPECIALITE_IDS.includes(m.id))
    .map((m) => ({ value: m.id, label: m.nom ?? m.id }));
  const metierLabelById: Record<string, string> = Object.fromEntries(
    metiersData.map((m) => [m.id, m.nom ?? m.id]),
  );

  const [modalOpen, setModalOpen] = useState(false);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<ProfileRow | null>(null);
  const [editingMember, setEditingMember] = useState<ProfileRow | null>(null);
  const [menuOpenId, setMenuOpenId] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  // Renvoi d'invitation (magic link) à un user existant
  const { mutate: resendInvite } = useResendInvite();
  const [resendingForId, setResendingForId] = useState<string | null>(null);
  const [resendFeedback, setResendFeedback] = useState<{ id: string; type: "success" | "error"; msg: string } | null>(null);

  const handleResendInvite = (member: ProfileRow) => {
    if (!member.email) {
      setResendFeedback({ id: member.id, type: "error", msg: t("parametres.equipe.resendNoEmail") });
      setMenuOpenId(null);
      setTimeout(() => setResendFeedback((f) => (f && f.id === member.id ? null : f)), 3500);
      return;
    }
    setResendingForId(member.id);
    setMenuOpenId(null);
    resendInvite(member.email, {
      onSuccess: () => {
        setResendFeedback({ id: member.id, type: "success", msg: t("parametres.equipe.resendSent") });
        setTimeout(() => setResendFeedback((f) => (f && f.id === member.id ? null : f)), 3500);
      },
      onError: (err) => {
        setResendFeedback({ id: member.id, type: "error", msg: err instanceof Error ? err.message : t("parametres.equipe.resendError") });
        setTimeout(() => setResendFeedback((f) => (f && f.id === member.id ? null : f)), 5000);
      },
      onSettled: () => setResendingForId(null),
    });
  };

  const [formData, setFormData] = useState({
    first_name: "",
    last_name: "",
    email: "",
    telephone: "",
    role: "collaborateur",
    statut: "actif" as "actif" | "inactif",
    societe: "",
    specialites: [] as string[],
  });

  const internalMembers = members.filter((m) => INTERNAL_ROLES.includes(m.role));
  const externalMembers = members.filter((m) => EXTERNAL_ROLES.includes(m.role));

  const roleOptions = [
    { value: "admin", label: t("parametres.equipe.roles.admin") },
    { value: "c_level", label: "C. Level" },
    { value: "publication", label: t("parametres.equipe.roles.publication") },
    { value: "collaborateur", label: t("parametres.equipe.roles.collaborateur") },
    { value: "vente", label: t("parametres.equipe.roles.vente") },
    { value: "prestataire", label: t("parametres.equipe.roles.prestataire") },
    { value: "autre", label: t("parametres.equipe.roles.autres") },
  ];

  const statusOptions = [
    { value: "actif", label: t("parametres.equipe.statuses.actif") },
    { value: "inactif", label: t("parametres.equipe.statuses.inactif") },
  ];

  const openEditModal = (member: ProfileRow) => {
    setEditingMember(member);
    setFormData({
      first_name: member.first_name ?? "",
      last_name: member.last_name ?? "",
      email: member.email,
      telephone: member.telephone ?? "",
      role: member.role,
      statut: (member.statut ?? "actif") as "actif" | "inactif",
      societe: member.societe ?? "",
      specialites: member.specialites ?? [],
    });
    setModalOpen(true);
    setMenuOpenId(null);
  };

  const closeModal = () => {
    setModalOpen(false);
    setEditingMember(null);
  };

  const handleSave = async () => {
    if (!editingMember) return;
    setIsSaving(true);
    try {
      await profilesRepository.update(editingMember.id, {
        first_name: formData.first_name,
        last_name: formData.last_name,
        telephone: formData.telephone,
        role: formData.role,
        statut: formData.statut,
        societe: formData.societe,
        specialites: formData.specialites,
      });
      qc.invalidateQueries({ queryKey: profilesKeys.all });
      closeModal();
    } catch (err) {
      console.error(err);
    } finally {
      setIsSaving(false);
    }
  };

  const formatSpecialites = (specialites?: string[] | null): string => {
    if (!specialites || specialites.length === 0) return "—";
    // Priorité au label lu depuis la table `metiers` (source dynamique).
    // Fallback sur la clé i18n pour les valeurs legacy (expert_peb, etc.)
    // encore présentes dans profiles.specialites avant la migration underscore→dash.
    return specialites
      .map((s) => metierLabelById[s] ?? t(`parametres.equipe.specialitesOptions.${s}`, { defaultValue: s }))
      .join(", ");
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 size={24} className="animate-spin text-accent" />
      </div>
    );
  }
  if (isError) {
    return (
      <div className="bg-danger-light border border-danger rounded-md p-4">
        <p className="text-sm text-danger font-medium">{t("parametres.equipe.loadError")}</p>
      </div>
    );
  }

  return (
    <div>
      {/* Header : bouton "Inviter un membre" */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-start gap-2 px-3 py-2 bg-surface-2 border border-dashed border-border rounded-md text-xs text-text-muted flex-1">
          <Info size={14} className="shrink-0 mt-0.5 text-text-muted" />
          <span>{t("parametres.equipe.infoInviteFlow")}</span>
        </div>
        <Button variant="primary" size="sm" onClick={() => setInviteOpen(true)} className="ml-3">
          <UserPlus size={14} />
          {t("parametres.equipe.inviteMember")}
        </Button>
      </div>

      <InviteMemberModal isOpen={inviteOpen} onClose={() => setInviteOpen(false)} />
      <DeleteMemberModal target={deleteTarget} members={members} onClose={() => setDeleteTarget(null)} />

      {resendFeedback && (
        <div className={cn(
          "flex items-center gap-2 px-3 py-2 mb-4 rounded-md text-sm border",
          resendFeedback.type === "success"
            ? "bg-success-light text-success border-success/30"
            : "bg-danger-light text-danger border-danger/30",
        )}>
          {resendFeedback.type === "success" ? <CheckCircle size={14} /> : <AlertTriangle size={14} />}
          <span>{resendFeedback.msg}</span>
        </div>
      )}

      {/* Table interne */}
      <h2 className="text-xs font-semibold tracking-widest uppercase text-text-secondary mb-3">
        {t("parametres.equipe.internalTeam")}
      </h2>
      <div className="bg-surface border border-border rounded-lg overflow-hidden mb-8">
        <table className="w-full">
          <thead>
            <tr className="border-b border-border bg-surface-2">
              <th className="text-left px-4 py-3 text-xs font-medium tracking-wider uppercase text-text-muted">{t("parametres.equipe.member")}</th>
              <th className="text-left px-4 py-3 text-xs font-medium tracking-wider uppercase text-text-muted">{t("common.email")}</th>
              <th className="text-left px-4 py-3 text-xs font-medium tracking-wider uppercase text-text-muted">{t("common.phone")}</th>
              <th className="text-left px-4 py-3 text-xs font-medium tracking-wider uppercase text-text-muted">{t("parametres.equipe.role")}</th>
              <th className="text-left px-4 py-3 text-xs font-medium tracking-wider uppercase text-text-muted">{t("common.status")}</th>
              <th className="w-12" />
            </tr>
          </thead>
          <tbody>
            {internalMembers.map((member) => (
              <tr key={member.id} className="border-b border-border last:border-b-0 hover:bg-surface-2 transition-colors">
                <td className="px-4 py-3">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-gray-300 flex items-center justify-center text-text-muted text-xs font-medium shrink-0">
                      {getInitials(member.first_name ?? "", member.last_name ?? "")}
                    </div>
                    <span className="text-sm font-medium text-text-primary">
                      {member.first_name} {member.last_name}
                    </span>
                  </div>
                </td>
                <td className="px-4 py-3 text-xs text-text-secondary">{member.email || "—"}</td>
                <td className="px-4 py-3 text-xs text-text-secondary">{member.telephone || "—"}</td>
                <td className="px-4 py-3">
                  <Badge variant={ROLE_BADGE_VARIANT[member.role] ?? "default"}>
                    {t(`parametres.equipe.roles.${member.role}`, { defaultValue: member.role })}
                  </Badge>
                </td>
                <td className="px-4 py-3">
                  {member.statut === "actif" && <div className="w-2.5 h-2.5 rounded-full bg-success" />}
                </td>
                <td className="px-4 py-3">
                  <MemberActions
                    isOpen={menuOpenId === member.id}
                    onToggle={() => setMenuOpenId(menuOpenId === member.id ? null : member.id)}
                    onEdit={() => openEditModal(member)}
                    onResendInvite={() => handleResendInvite(member)}
                    resendDisabled={!member.email}
                    resending={resendingForId === member.id}
                    onDelete={() => { setDeleteTarget(member); setMenuOpenId(null); }}
                    onClose={() => setMenuOpenId(null)}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Table externe (prestataires & autres) */}
      <h2 className="text-xs font-semibold tracking-widest uppercase text-text-secondary mb-3">
        {t("parametres.equipe.externalTeam")}
      </h2>
      <div className="bg-surface border border-border rounded-lg overflow-hidden">
        <table className="w-full">
          <thead>
            <tr className="border-b border-border bg-surface-2">
              <th className="text-left px-4 py-3 text-xs font-medium tracking-wider uppercase text-text-muted">{t("parametres.equipe.member")}</th>
              <th className="text-left px-4 py-3 text-xs font-medium tracking-wider uppercase text-text-muted">{t("parametres.equipe.societe")}</th>
              <th className="text-left px-4 py-3 text-xs font-medium tracking-wider uppercase text-text-muted">{t("parametres.equipe.specialites")}</th>
              <th className="text-left px-4 py-3 text-xs font-medium tracking-wider uppercase text-text-muted">{t("common.email")}</th>
              <th className="text-left px-4 py-3 text-xs font-medium tracking-wider uppercase text-text-muted">{t("common.phone")}</th>
              <th className="text-left px-4 py-3 text-xs font-medium tracking-wider uppercase text-text-muted">{t("parametres.equipe.role")}</th>
              <th className="text-left px-4 py-3 text-xs font-medium tracking-wider uppercase text-text-muted">{t("common.status")}</th>
              <th className="w-12" />
            </tr>
          </thead>
          <tbody>
            {externalMembers.map((member) => (
              <tr key={member.id} className="border-b border-border last:border-b-0 hover:bg-surface-2 transition-colors">
                <td className="px-4 py-3">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-gray-300 flex items-center justify-center text-text-muted text-xs font-medium shrink-0">
                      {getInitials(member.first_name ?? "", member.last_name ?? "")}
                    </div>
                    <span className="text-sm font-medium text-text-primary">
                      {member.first_name} {member.last_name}
                    </span>
                  </div>
                </td>
                <td className="px-4 py-3 text-xs text-text-secondary">{member.societe || "—"}</td>
                <td className="px-4 py-3 text-xs text-text-secondary">{formatSpecialites(member.specialites)}</td>
                <td className="px-4 py-3 text-xs text-text-secondary">{member.email || "—"}</td>
                <td className="px-4 py-3 text-xs text-text-secondary">{member.telephone || "—"}</td>
                <td className="px-4 py-3">
                  <Badge variant={ROLE_BADGE_VARIANT[member.role] ?? "default"}>
                    {t(`parametres.equipe.roles.${member.role}`, { defaultValue: member.role })}
                  </Badge>
                </td>
                <td className="px-4 py-3">
                  {member.statut === "actif" && <div className="w-2.5 h-2.5 rounded-full bg-success" />}
                </td>
                <td className="px-4 py-3">
                  <MemberActions
                    isOpen={menuOpenId === member.id}
                    onToggle={() => setMenuOpenId(menuOpenId === member.id ? null : member.id)}
                    onEdit={() => openEditModal(member)}
                    onResendInvite={() => handleResendInvite(member)}
                    resendDisabled={!member.email}
                    resending={resendingForId === member.id}
                    onDelete={() => { setDeleteTarget(member); setMenuOpenId(null); }}
                    onClose={() => setMenuOpenId(null)}
                    deleteLabel={member.role === "prestataire" ? t("parametres.equipe.deactivateMenu") : undefined}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Modal édition (création via Supabase dashboard uniquement) */}
      <Modal isOpen={modalOpen} onClose={closeModal} title={t("parametres.equipe.editTitle")}>
        <div className="flex flex-col gap-4">
          <div className="grid grid-cols-2 gap-4">
            <FormField
              label={t("parametres.equipe.firstName")}
              value={formData.first_name}
              onChange={(v) => setFormData((prev) => ({ ...prev, first_name: v }))}
            />
            <FormField
              label={t("parametres.equipe.lastName")}
              value={formData.last_name}
              onChange={(v) => setFormData((prev) => ({ ...prev, last_name: v }))}
            />
          </div>

          <FormField
            label={t("common.email")}
            value={formData.email}
            onChange={() => {}}
            readOnly
          />

          <FormField
            label={t("common.phone")}
            value={formData.telephone}
            onChange={(v) => setFormData((prev) => ({ ...prev, telephone: v }))}
          />

          <div className="grid grid-cols-2 gap-4">
            <SelectField
              label={t("parametres.equipe.role")}
              value={formData.role}
              options={roleOptions}
              onChange={(v) => setFormData((prev) => ({
                ...prev,
                role: v,
                ...(v !== "prestataire" ? { societe: "", specialites: [] } : {}),
              }))}
            />
            <SelectField
              label={t("common.status")}
              value={formData.statut}
              options={statusOptions}
              onChange={(v) => setFormData((prev) => ({ ...prev, statut: v as "actif" | "inactif" }))}
            />
          </div>

          {formData.role === "prestataire" && (
            <>
              <FormField
                label={t("parametres.equipe.societe")}
                value={formData.societe}
                onChange={(v) => setFormData((prev) => ({ ...prev, societe: v }))}
              />
              {/* Mono-spécialité : un prestataire = un seul domaine
                  (ex. ELS ou PEB), pas plusieurs. La donnée reste stockée
                  comme un array (vide ou avec 1 entrée) pour ne pas casser
                  les RPC qui filtrent sur ce champ. */}
              <SelectField
                label={t("parametres.equipe.specialites")}
                value={formData.specialites[0] ?? ""}
                onChange={(v) => setFormData((prev) => ({ ...prev, specialites: v ? [v] : [] }))}
                options={specialitesOptions}
              />
            </>
          )}

          <div className="flex items-center justify-center gap-3 pt-3">
            <Button variant="cancel" onClick={closeModal}>
              {t("common.cancel")}
            </Button>
            <Button variant="primary" onClick={handleSave} disabled={isSaving}>
              {isSaving ? t("common.saving") : t("common.save")}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}

// ─── InviteMemberModal ──────────────────────────────────────────────────────
// Formulaire de création d'un user EN UNE SEULE ÉTAPE via l'Edge Function
// `invite-user`. Capte tous les champs (dont téléphone et, pour un prestataire,
// la spécialité PEB/ELS) → plus besoin d'une 2e modale d'édition après coup.
// Nécessite que la fonction soit déployée + SUPABASE_SERVICE_ROLE_KEY configuré
// dans les secrets Edge Function.

function InviteMemberModal({ isOpen, onClose }: { isOpen: boolean; onClose: () => void }) {
  const { t } = useTranslation();
  const { mutate: invite, isPending } = useInviteUser();

  // Spécialités proposées : PEB + ELS uniquement (cf. INVITE_SPECIALITE_IDS).
  const { data: metiersData = [] } = useMetiers();
  const specialitesOptions = metiersData
    .filter((m) => m.actif !== false && INVITE_SPECIALITE_IDS.includes(m.id))
    .map((m) => ({ value: m.id, label: m.nom ?? m.id }));

  const [email, setEmail] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [telephone, setTelephone] = useState("");
  const [role, setRole] = useState("");
  const [societe, setSociete] = useState("");
  const [specialite, setSpecialite] = useState("");
  const [feedback, setFeedback] = useState<{ type: "success" | "error"; msg: string } | null>(null);

  const isPrestataire = role === "prestataire";
  // Requis : email, prénom, nom, téléphone, rôle (+ spécialité si prestataire).
  const canSubmit =
    !!email.trim() &&
    !!firstName.trim() &&
    !!lastName.trim() &&
    !!telephone.trim() &&
    !!role &&
    (!isPrestataire || !!specialite);

  const reset = () => {
    setEmail("");
    setFirstName("");
    setLastName("");
    setTelephone("");
    setRole("");
    setSociete("");
    setSpecialite("");
    setFeedback(null);
  };

  // Traduit l'erreur (brute) renvoyée par l'Edge Function en message propre.
  // Garde le détail technique en console pour le debug.
  const friendlyError = (raw: string): string => {
    const m = raw.toLowerCase();
    if (m.includes("validate email") || m.includes("invalid format") || m.includes("invalid email"))
      return t("parametres.equipe.inviteInvalidEmail");
    if (m.includes("already") || m.includes("registered") || m.includes("exists") || m.includes("duplicate"))
      return t("parametres.equipe.inviteEmailExists");
    return t("parametres.equipe.inviteGenericError");
  };

  const handleSubmit = () => {
    if (!canSubmit) return;
    // Validation du format email côté client → évite un aller-retour serveur
    // et un message technique pour un email manifestement invalide.
    const trimmedEmail = email.trim();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedEmail)) {
      setFeedback({ type: "error", msg: t("parametres.equipe.inviteInvalidEmail") });
      return;
    }
    setFeedback(null);
    invite(
      {
        email: trimmedEmail,
        first_name: firstName.trim(),
        last_name: lastName.trim(),
        telephone: telephone.trim(),
        role,
        ...(isPrestataire
          ? { societe: societe.trim(), specialites: specialite ? [specialite] : [] }
          : {}),
      },
      {
        onSuccess: (res) => {
          if (res.emailSent) {
            setFeedback({ type: "success", msg: t("parametres.equipe.inviteSent") });
            setTimeout(() => { reset(); onClose(); }, 1500);
          } else {
            // Compte créé mais email non parti → on prévient, sans fermer.
            setFeedback({ type: "error", msg: t("parametres.equipe.inviteEmailFailed") });
          }
        },
        onError: (err) => {
          console.error("[invite-user]", err.message);
          setFeedback({ type: "error", msg: friendlyError(err.message) });
        },
      }
    );
  };

  return (
    <Modal isOpen={isOpen} onClose={() => { reset(); onClose(); }} title={t("parametres.equipe.inviteMember")}>
      <div className="flex flex-col gap-4">
        <FormField label={t("common.email")} value={email} onChange={setEmail} missingHint="required" />
        <div className="grid grid-cols-2 gap-4">
          <FormField label={t("parametres.equipe.firstName")} value={firstName} onChange={setFirstName} missingHint="required" />
          <FormField label={t("parametres.equipe.lastName")} value={lastName} onChange={setLastName} missingHint="required" />
        </div>
        <FormField label={t("common.phone")} value={telephone} onChange={setTelephone} missingHint="required" />
        <SelectField
          label={t("parametres.equipe.role")}
          value={role}
          missingHint="required"
          onChange={(v) => {
            setRole(v);
            if (v !== "prestataire") { setSociete(""); setSpecialite(""); }
          }}
          options={[
            { value: "admin",          label: t("parametres.equipe.roles.admin") },
            { value: "c_level",        label: "C. Level" },
            { value: "collaborateur",  label: t("parametres.equipe.roles.collaborateur") },
            { value: "vente",          label: t("parametres.equipe.roles.vente") },
            { value: "publication",    label: t("parametres.equipe.roles.publication") },
            { value: "prestataire",    label: t("parametres.equipe.roles.prestataire") },
          ]}
        />
        {isPrestataire && (
          <>
            <FormField label={t("parametres.equipe.societe")} value={societe} onChange={setSociete} />
            <SelectField
              label={t("parametres.equipe.specialites")}
              value={specialite}
              onChange={setSpecialite}
              options={specialitesOptions}
              missingHint="required"
            />
          </>
        )}
        {feedback && (
          <div className={cn(
            "flex items-center gap-2 text-xs px-3 py-2 rounded-md",
            feedback.type === "success" ? "bg-success/10 text-success" : "bg-danger/10 text-danger"
          )}>
            {feedback.type === "success"
              ? <CheckCircle size={14} />
              : <AlertTriangle size={14} />}
            <span>{feedback.msg}</span>
          </div>
        )}
        <div className="flex justify-end gap-2 pt-2">
          <button type="button" onClick={() => { reset(); onClose(); }} className="px-3 py-2 text-sm text-text-muted hover:text-text-primary cursor-pointer">
            {t("common.cancel")}
          </button>
          <Button variant="primary" size="sm" onClick={handleSubmit} disabled={isPending || !canSubmit}>
            {isPending ? t("common.saving") : t("parametres.equipe.sendInvite")}
          </Button>
        </div>
      </div>
    </Modal>
  );
}

// ─── DeleteMemberModal ──────────────────────────────────────────────────────
// Confirmation de suppression d'un user via l'Edge Function `delete-user`.

function DeleteMemberModal({ target, members, onClose }: { target: ProfileRow | null; members: ProfileRow[]; onClose: () => void }) {
  const { t } = useTranslation();
  const isPrestataire = target?.role === "prestataire";
  const { mutate: deleteUser, isPending: deleting } = useDeleteUser();
  const { mutate: deactivatePrestataire, isPending: deactivating } = useDeactivatePrestataire();
  // Comptage des biens (collaborateurSrs) inutile pour un prestataire → désactivé.
  const { data: assignedCount, isLoading: countLoading } = useAssignedBiensCount(
    !isPrestataire ? (target?.id ?? null) : null,
  );
  const [error, setError] = useState<string | null>(null);
  const [reassignTo, setReassignTo] = useState("");
  // Garde-fou serveur : si le delete renvoie 409 has_assignments (dry_run KO ou
  // course), on bascule en mode réassignation avec le compte renvoyé par le serveur.
  const [serverCount, setServerCount] = useState(0);

  // Reset à chaque ouverture sur une nouvelle cible.
  useEffect(() => { setReassignTo(""); setError(null); setServerCount(0); }, [target?.id]);

  if (!target) return null;

  const isPending = deleting || deactivating;
  const displayName = `${target.first_name ?? ""} ${target.last_name ?? ""}`.trim() || target.email || target.id;
  const count = serverCount || (assignedCount ?? 0);
  const needsReassign = count > 0;

  // ── Prestataire : on DÉSACTIVE (jamais de hard delete). Ses missions ELS/PEB
  //    en cours sont conservées ; il sort des dropdowns ; les biens loués/incomplets
  //    où il était assigné repassent sur « - » (RPC admin_deactivate_prestataire).
  if (isPrestataire) {
    const handleDeactivate = () => {
      setError(null);
      deactivatePrestataire(target.id, {
        onSuccess: () => onClose(),
        onError: (err) => setError(err instanceof Error ? err.message : String(err)),
      });
    };
    return (
      <Modal isOpen={!!target} onClose={() => { setError(null); onClose(); }} title={t("parametres.equipe.deactivateTitle")}>
        <div className="flex flex-col gap-4">
          <p className="text-sm text-text-primary">
            {t("parametres.equipe.deactivateConfirm", { name: displayName })}
          </p>
          <div className="flex items-start gap-2 text-xs px-3 py-2 rounded-md bg-warning-light text-warning">
            <AlertTriangle size={14} className="shrink-0 mt-0.5" />
            <span>{t("parametres.equipe.deactivateInfo")}</span>
          </div>
          {error && (
            <div className="flex items-center gap-2 text-xs px-3 py-2 rounded-md bg-danger/10 text-danger">
              <AlertTriangle size={14} />
              <span>{error}</span>
            </div>
          )}
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={() => { setError(null); onClose(); }} className="px-3 py-2 text-sm text-text-muted hover:text-text-primary cursor-pointer">
              {t("common.cancel")}
            </button>
            <Button variant="primary" size="sm" onClick={handleDeactivate} disabled={isPending}>
              {deactivating ? t("common.saving") : t("parametres.equipe.deactivateAction")}
            </Button>
          </div>
        </div>
      </Modal>
    );
  }

  // Repreneurs possibles : les autres collaborateurs (la boucle collab se pilote
  // par collaborateurSrs, donc seul un collaborateur peut reprendre le batch).
  const reassignOptions = members
    .filter((m) => m.id !== target.id && (m.role ?? "").startsWith("collaborateur"))
    .map((m) => ({
      value: m.id,
      label: `${m.first_name ?? ""} ${m.last_name ?? ""}`.trim() || m.email || m.id,
    }));
  const noReassignTarget = needsReassign && reassignOptions.length === 0;

  const confirmDisabled =
    isPending || countLoading || (needsReassign && (!reassignTo || noReassignTarget));

  const handleConfirm = () => {
    setError(null);
    deleteUser(
      { user_id: target.id, reassign_to: needsReassign ? reassignTo : undefined },
      {
        onSuccess: () => { onClose(); },
        onError: (err) => {
          const e = err as Error & { code?: string; assigned_count?: number };
          // Le serveur a refusé car des biens sont assignés → on force le mode
          // réassignation (même si le dry_run avait échoué silencieusement).
          if (e.code === "has_assignments" || e.message === "has_assignments") {
            setServerCount(e.assigned_count || 1);
            setError(null);
          } else {
            setError(err.message);
          }
        },
      },
    );
  };

  return (
    <Modal isOpen={!!target} onClose={() => { setError(null); onClose(); }} title={t("parametres.equipe.deleteTitle")}>
      <div className="flex flex-col gap-4">
        <p className="text-sm text-text-primary">
          {t("parametres.equipe.deleteConfirm", { name: displayName })}
        </p>

        {needsReassign ? (
          <>
            <div className="flex items-start gap-2 text-xs px-3 py-2 rounded-md bg-warning-light text-warning">
              <AlertTriangle size={14} className="shrink-0 mt-0.5" />
              <span>{t("parametres.equipe.deleteReassignIntro", { count })}</span>
            </div>
            {noReassignTarget ? (
              <p className="text-xs text-danger">{t("parametres.equipe.deleteNoCollab")}</p>
            ) : (
              <SelectField
                label={t("parametres.equipe.deleteReassignLabel")}
                value={reassignTo}
                missingHint={reassignTo ? undefined : "required"}
                onChange={setReassignTo}
                options={reassignOptions}
              />
            )}
          </>
        ) : (
          <p className="text-xs text-text-muted">{t("parametres.equipe.deleteWarning")}</p>
        )}

        {error && (
          <div className="flex items-center gap-2 text-xs px-3 py-2 rounded-md bg-danger/10 text-danger">
            <AlertTriangle size={14} />
            <span>{error}</span>
          </div>
        )}
        <div className="flex justify-end gap-2 pt-2">
          <button type="button" onClick={() => { setError(null); onClose(); }} className="px-3 py-2 text-sm text-text-muted hover:text-text-primary cursor-pointer">
            {t("common.cancel")}
          </button>
          <Button variant="primary" size="sm" onClick={handleConfirm} disabled={confirmDisabled}>
            {isPending
              ? t("common.saving")
              : needsReassign
                ? t("parametres.equipe.deleteReassignAction")
                : t("common.delete")}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
